/**
 * The card index Configure opens on.
 *
 * A dropdown told you which plugin you were editing and nothing else. The two
 * numbers an operator actually needs before opening anything are on the card:
 * how many fields this tenant has overridden, and how many it is allowed to.
 * "all inherited · 4 unlocked" and "1 override · 4 unlocked" are different
 * situations, and a picker made them indistinguishable until you opened each in
 * turn.
 *
 * Each card fetches its own two counts. That is a request per plugin, but they
 * are the same queries the field editor needs when the card is opened, so the
 * drill-down is served from cache rather than paying for them twice.
 */
import { ApiError } from '@optum/api-client/client';
import { getCapabilityGlobalConfig } from '@optum/api-client/endpoints/capability-global-configs';
import { getOrgCapabilityConfig } from '@optum/api-client/endpoints/org-capability-configs';
import type { FieldScopes } from '@optum/api-client/types';
import { useQuery } from '@tanstack/react-query';

import { qk } from '../../lib/query-keys.js';
import { Badge, Icon } from '../../ui/finixt.js';

export interface ConfigItem {
  id: string;
  name: string;
  version: string;
  description?: string | undefined;
}

/** A missing global config is a 404, not a failure — nothing has been set yet. */
const notFound = (n: number, e: unknown) =>
  e instanceof ApiError && e.status === 404 ? false : n < 2;

interface GlobalConfigShape {
  config: Record<string, unknown>;
  field_scopes: FieldScopes;
}

interface StoredConfigShape {
  config: Record<string, unknown>;
}

function unlockedCount(scopes: FieldScopes): number {
  return Object.values(scopes).filter((s) => s.organization === true).length;
}

export function ConfigCardIndex({
  organizationId,
  items,
  onSelect,
}: {
  organizationId: string;
  items: ConfigItem[];
  onSelect: (id: string) => void;
}) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 11 }}>
      {items.map((item) => (
        <ConfigCard key={item.id} organizationId={organizationId} item={item} onSelect={onSelect} />
      ))}
    </div>
  );
}

function ConfigCard({
  organizationId,
  item,
  onSelect,
}: {
  organizationId: string;
  item: ConfigItem;
  onSelect: (id: string) => void;
}) {
  const global = useQuery({
    queryKey: qk.capabilityGlobalConfig(item.id),
    queryFn: (): Promise<GlobalConfigShape> => getCapabilityGlobalConfig(item.id),
    retry: notFound,
  });
  const stored = useQuery({
    queryKey: qk.orgCapConfig(organizationId, item.id),
    queryFn: (): Promise<StoredConfigShape> => getOrgCapabilityConfig(organizationId, item.id),
    retry: notFound,
  });

  const scopes = global.data?.field_scopes ?? {};
  const unlocked = unlockedCount(scopes);
  // Only overrides on fields still unlocked count: a stored value for a field
  // since locked to global is ignored at task time and is not editable here.
  const overrides = Object.keys(stored.data?.config ?? {}).filter(
    (name) => scopes[name]?.organization === true,
  ).length;
  const counted = global.isSuccess && stored.isSuccess;

  return (
    <button
      type="button"
      onClick={() => onSelect(item.id)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 11,
        borderRadius: 'var(--radius-card)',
        border: '1px solid var(--border-hairline)',
        background: 'var(--surface-card)',
        padding: 16,
        textAlign: 'left',
      }}
    >
      <span style={{ display: 'flex', alignItems: 'flex-start', gap: 11 }}>
        <span
          style={{
            display: 'flex',
            width: 32,
            height: 32,
            flexShrink: 0,
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 'var(--radius-input)',
            background: 'var(--blue-tint)',
            color: 'var(--blue)',
          }}
        >
          <Icon name="extension" size={15} />
        </span>
        <span style={{ minWidth: 0, flex: '1 1 0%' }}>
          <span
            style={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: 'var(--text-meta-lg)',
              fontWeight: 'var(--weight-bold)',
              color: 'var(--text-strong)',
            }}
            className="mono"
          >
            {item.id}
          </span>
          <span
            style={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: 'var(--text-meta-lg)',
              color: 'var(--text-muted)',
            }}
            className="mono"
          >
            v{item.version}
          </span>
        </span>
      </span>

      {item.description && (
        <span
          style={{ fontSize: 'var(--text-meta-lg)', lineHeight: 1.625, color: 'var(--text-faint)' }}
          className="clamp2"
        >
          {item.description}
        </span>
      )}

      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          borderTop: '1px solid var(--border-hairline)',
          borderColor: 'var(--border-hairline)',
          paddingTop: 11,
        }}
      >
        {counted ? (
          <>
            {overrides > 0 ? (
              <Badge tone="blue">{`${overrides} override${overrides === 1 ? '' : 's'}`}</Badge>
            ) : (
              <Badge tone="neutral">all inherited</Badge>
            )}
            <span style={{ flex: '1 1 0%' }} />
            <span
              style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-faint)' }}
              className="mono"
            >
              {unlocked} unlocked
            </span>
          </>
        ) : (
          <span
            style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-faint)' }}
            className="mono"
          >
            …
          </span>
        )}
      </span>
    </button>
  );
}
